import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard, getScheduleInstanceFromViewManager } from "@/other/utils/otherUtils";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Client } from "@/bryntum-lib/models/Client";
import { Contact } from "@/bryntum-lib/models/Contact";
import { User } from "@/bryntum-lib/models/User";
import { TaskBudget } from "@/bryntum-lib/models/TaskBudget";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { Task } from "@/bryntum-lib/models/Task";
import { TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import TaskDrag from "@/bryntum-lib/widget/extendingClasses/TaskDrag";
import { swivuRecentRecordSortFn } from "@/modern/utils";

interface EntityFinderContextValue {
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchClear: () => void;
  handleTabChange: (value: string) => void;
  isShowArchive: boolean;
  searchValue: string;
  data: any[];
  activeTab: string;
  handleClose: () => void;
}

const EntityFinderContext = createContext<EntityFinderContextValue | undefined>(undefined);

interface EntityFinderProviderProps {
  children: ReactNode;
  record: any;
  onClose: () => void;
}

export const EntityFinderProvider: React.FC<EntityFinderProviderProps> = ({ children, onClose }) => {
  const { project, user, viewManager } = useSwivuBryntumContext();
  const currentBoardUser = useMemo(
    () => project?.boardUsersStore?.find((boardUser: any) => boardUser.userId === user?.id),
    [project, user?.id]
  );
  const [activeTab, setActiveTab] = useState("tasks");
  const [searchValue, setSearchValue] = useState("");
  const taskDragRef = useRef<TaskDrag | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
  }, []);

  const isShowArchive = useMemo(() => {
    const selectedBoard = getSavedBoardSelection();
    return getIsArchivedForBoard(selectedBoard.slug);
  }, []);

  const data = useMemo(() => {
    const searchLower = searchValue.toLowerCase().trim();

    const matchesSearch = (value: any): boolean => {
      if (!searchLower) return true;
      if (!value) return false;
      return String(value).toLowerCase().includes(searchLower);
    };

    switch (activeTab) {
      case "tasks": {
        const tasks = project.extraTaskStore.query((task: Task) => (isShowArchive || !task.archived) && !task.isLinked && (matchesSearch(task.name) || matchesSearch(task.description)), true) as unknown as Task[];
        return tasks.sort((a, b) => {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
      }
      case "resources": {
        const resources = project.resourceStore.query(
          (resource: Resource) => (isShowArchive || !resource.archived) && !resource.isLinked && (matchesSearch(resource.name) || matchesSearch(resource.email)), true
        ) as unknown as Resource[];
        return swivuRecentRecordSortFn(resources, { currentBoardUserId: currentBoardUser?.id });
      }
      case "clients": {
        const clients = project.clientsStore.query(
          (client: Client) => (isShowArchive || !client.archived) && !client.isLinked && (matchesSearch(client.name) || matchesSearch(client.email)), true
        ) as unknown as Client[];
        return swivuRecentRecordSortFn(clients, { currentBoardUserId: currentBoardUser?.id });
      }
      case "contacts": {
        const contacts = project.contactsStore.query(
          (contact: Contact) => (isShowArchive || !contact.archived) && !contact.isLinked && (matchesSearch(contact.name) || matchesSearch(contact.email)), true
        ) as unknown as Contact[];
        return swivuRecentRecordSortFn(contacts, {
          currentBoardUserId: currentBoardUser?.id,
          getUpdatedByField: (r) => (r as Contact).lastUpdatedBy,
          getUpdatedAtField: (r) => (r as Contact).lastUpdatedAt
        });
      }
      case "users": {
        const users = project.boardUsersStore.query((u: User) => (isShowArchive || !u.archived) && !u.isLinked && (matchesSearch(u.name) || matchesSearch(u.email)), true) as unknown as User[];
        return swivuRecentRecordSortFn(users, { disableUpdatedAtSort: true, disableUpdatedBySort: true });
      }
      case "budgets": {
        const budgets = project.taskBudgetStore.query(
          (budget: TaskBudget) => (isShowArchive || !budget.archived) && !budget.isLinked && (matchesSearch(budget[TASK_BUDGET_FIELDS.title]) || matchesSearch(budget[TASK_BUDGET_FIELDS.description])), true
        ) as unknown as TaskBudget[];
        return swivuRecentRecordSortFn(budgets, {
          currentBoardUserId: currentBoardUser?.id,
          nameField: TASK_BUDGET_FIELDS.title
        });
      }
      case "checklists": {
        const checklists = project.checklistStore.query(
          (checklist: Checklist) => (isShowArchive || !checklist.archived) && !checklist.isLinked && (matchesSearch(checklist.name) || matchesSearch(checklist.title)), true
        ) as unknown as Checklist[];
        return swivuRecentRecordSortFn(checklists, { nameField: "title", getUpdatedByField: (r) => (r as Checklist).updater, getUpdatedAtField: (r) => (r as Checklist).createdAt });
      }
      default:
        return [];
    }
  }, [project, user?.id, currentBoardUser?.id, isShowArchive, activeTab, searchValue]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!taskDragRef.current && viewManager) {
      if (viewManager.pageViewsRef) {
        const schedularInstance = getScheduleInstanceFromViewManager(viewManager);
        if (schedularInstance) {
          taskDragRef.current = new TaskDrag({
            // @ts-expect-error getRecordFromElement is not in the type definition
            grid: {
              getRecordFromElement: (el: any) => {
                const taskId = el.dataset.swivuTaskId;
                return project.extraTaskStore.getById(taskId);
              }
            },
            schedule: schedularInstance,
            constrain: false,
            targetSelector: ".swivu-entity-finder-drag-handler"
          });
        }
      }
    }

    return () => {
      if (taskDragRef.current) {
        taskDragRef.current?.destroy?.();
        taskDragRef.current = null;
      }
    };
  }, [viewManager, project]);

  return (
    <EntityFinderContext.Provider value={{ handleSearchChange, handleSearchClear, handleTabChange, isShowArchive, data, searchValue, activeTab, handleClose }}>{children}</EntityFinderContext.Provider>
  );
};

export const useEntityFinderContext = (): EntityFinderContextValue => {
  const context = useContext(EntityFinderContext);
  if (!context) {
    throw new Error("useEntityFinderContext must be used within EntityFinderProvider");
  }
  return context;
};
